import { InterviewAgentType } from "@repo/app-types/entities/project";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import type { FC } from "react";
import { useNavigate } from "react-router";
import { appPaths } from "../../app/appPaths.js";
import { useAppDispatch } from "../../hooks/hook.js";
import { deleteProject } from "./projectsSlice.js";

interface DeleteProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  interviewAgentType: InterviewAgentType | null;
}

export const DeleteProjectModal: FC<DeleteProjectModalProps> = ({
  isOpen,
  onClose,
  projectId,
  interviewAgentType,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const onConfirmDelete = async () => {
    try {
      // First navigate back to the projects page before deleting so
      // that we don't try to a project that doesn't exist.
      navigate(
        interviewAgentType === InterviewAgentType.Training
          ? appPaths.trainings.build()
          : appPaths.home.build(),
      );

      // Hack to prevent race condition where the ProjectsTable lists projects before the delete is complete.
      setTimeout(() => {
        void dispatch(deleteProject(projectId));
      }, 2000);

      showToast({
        title: getDeleteConfirmationText(interviewAgentType),
        variant: ToastVariant.Success,
      });
    } catch (err) {
      console.error("error updating project", err);
    }

    onClose();
  };

  return (
    <SimpleModal
      isOpen={isOpen}
      variant={SimpleModalVariant.Warning}
      title={getDeleteProjectModalTitle(interviewAgentType)}
      subtitle={getDeleteProjectModalSubtitle(interviewAgentType)}
      confirmButtonText={getDeleteButtonText(interviewAgentType)}
      onCancel={onClose}
      onConfirm={onConfirmDelete}
    />
  );
};

function getDeleteProjectModalTitle(
  interviewAgentType: InterviewAgentType | null,
) {
  if (interviewAgentType === InterviewAgentType.RecruitingInterviewer) {
    return "Delete job opening";
  }

  if (interviewAgentType === InterviewAgentType.Training) {
    return "Delete training";
  }

  return "Delete project";
}

function getDeleteProjectModalSubtitle(
  interviewAgentType: InterviewAgentType | null,
) {
  if (interviewAgentType === InterviewAgentType.RecruitingInterviewer) {
    return "If you delete this job opening, past interviews will be permanently lost.";
  }

  if (interviewAgentType === InterviewAgentType.Training) {
    return "If you delete this training, past interviews will be permanently lost.";
  }

  return "If you delete this project, insights from past interviews will be permanently lost and any outstanding interviews will be cancelled.";
}

function getDeleteConfirmationText(
  interviewAgentType: InterviewAgentType | null,
) {
  if (interviewAgentType === InterviewAgentType.RecruitingInterviewer) {
    return "Job opening deleted";
  }

  if (interviewAgentType === InterviewAgentType.Training) {
    return "Training deleted";
  }

  return "Project deleted";
}

function getDeleteButtonText(interviewAgentType: InterviewAgentType | null) {
  if (interviewAgentType === InterviewAgentType.RecruitingInterviewer) {
    return "Delete job opening";
  }

  if (interviewAgentType === InterviewAgentType.Training) {
    return "Delete training";
  }

  return "Delete project";
}
